import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import TwitterButton from './TwitterButton';

const NavLink = ({ to, children, isActive }) => (
  <Link
    to={to}
    className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg
      ${isActive 
        ? 'text-gray-800 bg-[#D9F273]/20' 
        : 'text-gray-600 hover:text-gray-800 hover:bg-[#D9F273]/10'
      }
    `}
  >
    {children}
    {isActive && (
      <motion.div
        layoutId="navbar-active"
        className="absolute inset-0 rounded-lg bg-[#D9F273]/20 -z-10"
        transition={{ type: "spring", duration: 0.6 }}
      />
    )}
  </Link>
);

const Navbar = ({ walletAddress }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-xl shadow-lg' 
            : 'bg-white/50 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 group">
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <div className="flex items-center gap-2">
                <NavLink to="/" isActive={location.pathname === '/'}>
                  Dashboard
                </NavLink>
                <NavLink to="/features" isActive={location.pathname === '/features'}>
                  Features
                </NavLink>
                <NavLink to="/roadmap" isActive={location.pathname === '/roadmap'}>
                  Roadmap
                </NavLink>
              </div>

              {/* Wallet Display */}
              {walletAddress && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center px-4 py-2 bg-white/30 backdrop-blur-md rounded-lg border border-[#BFEA15]/20"
                >
                  <span className="text-sm text-gray-500">
                    Wallet: <span className="font-mono text-gray-800">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
                  </span>
                </motion.div>
              )}

              {/* Twitter Button */}
              <TwitterButton />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden relative w-10 h-10 focus:outline-none group"
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span 
                  className={`block h-0.5 w-6 bg-gray-600 transform transition-all duration-300 ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : '-translate-y-1'
                  }`}
                />
                <span 
                  className={`block h-0.5 w-6 bg-gray-600 transform transition-all duration-300 my-0.5 ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span 
                  className={`block h-0.5 w-6 bg-gray-600 transform transition-all duration-300 ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : 'translate-y-1'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-16 inset-x-0 z-40 md:hidden overflow-hidden"
          >
            <div className="mx-4 p-4 rounded-lg bg-white/90 backdrop-blur-xl shadow-lg border border-[#BFEA15]/20">
              <div className="flex flex-col gap-2">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === '/' 
                      ? 'bg-[#D9F273]/20 text-gray-800' 
                      : 'text-gray-600 hover:bg-[#D9F273]/10 hover:text-gray-800'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/features"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === '/features' 
                      ? 'bg-[#D9F273]/20 text-gray-800' 
                      : 'text-gray-600 hover:bg-[#D9F273]/10 hover:text-gray-800'
                  }`}
                >
                  Features
                </Link>
                <Link
                  to="/roadmap"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === '/roadmap' 
                      ? 'bg-[#D9F273]/20 text-gray-800' 
                      : 'text-gray-600 hover:bg-[#D9F273]/10 hover:text-gray-800'
                  }`}
                >
                  Roadmap
                </Link>
                {walletAddress && (
                  <div className="px-4 py-3 text-sm text-gray-500 bg-white/50 rounded-lg">
                    Wallet: <span className="font-mono text-gray-800">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
                  </div>
                )}
                <div className="px-4 py-3">
                  <TwitterButton />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
